/**
 * `useFileViewerCommandsFromAgent` — workspace-file viewer `lx_*` tools
 * from the concierge.
 *
 * Concierge slash-commands Part A §2.3 (handoff
 * `concierge-slash-commands-lexide-tools-handoff-2026-06-30.md`).
 *
 * Wires (Batch 1 — clean adds only):
 *   • `lx_close_file {}` → useWorkspaceFileStore.closeFile()
 *
 * Sibling tools (file versions / diff / info / select-text /
 * selection-chip) are 🔴 store-gap decisions deferred to Batch 3 —
 * they bind to `useCaseWorkspaceStore` / `useSelectionCapture()` /
 * `useChatStore`, NOT the fictional `useAiEditorStore` /
 * `useViewerInfoBarStore` / `useSelectionChipStore` the catalog §4.4
 * named. Wiring those needs design calls per the handoff §4 gap table.
 *
 * Pairs with the SHIPPED `useFileOpenFromAgent` (handles `lx_open_file`
 * via the same `useWorkspaceFileStore`). The two hooks coexist — same
 * store, different verbs.
 *
 * Mount once at App level (the file pane is global chrome).
 *
 * Contract: act on `phase === "start"` only; accept legacy field names.
 * Pure handler extracted for unit tests.
 */

import { useEventStream } from "./useEventStream";
import { useWorkspaceFileStore } from "@/stores/useWorkspaceFileStore";

export interface AgentEventPayloadRuntime {
  data?: {
    phase?: string;
    toolName?: string;
    name?: string;
    toolInput?: Record<string, unknown>;
    args?: Record<string, unknown>;
    [key: string]: unknown;
  };
}

export interface FileViewerCommandDeps {
  closeFile: () => void;
}

export function handleFileViewerCommand(
  payload: unknown,
  deps: FileViewerCommandDeps,
): void {
  const typed = payload as AgentEventPayloadRuntime;
  if (typed.data?.phase !== "start") return;
  const tool = typed.data?.toolName ?? typed.data?.name;

  switch (tool) {
    case "lx_close_file": {
      deps.closeFile();
      return;
    }
    default:
      return;
  }
}

export function useFileViewerCommandsFromAgent(): void {
  const closeFile = useWorkspaceFileStore((s) => s.closeFile);

  useEventStream("session.tool", (payload) => {
    handleFileViewerCommand(payload, { closeFile });
  });
}
